import { Box, LoadingDots } from '@flos/react-ui';
import { useRecoilValue } from 'recoil';
import { externalCasesAtom } from '../../recoil/atoms/externalCasesAtom';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { priceTaxAtom } from '../../recoil/atoms/priceTaxAtom';
import { paymentFrequencyMap, periodicityUnit } from '../../utils/currencyUtil';
import CustomerBenefit from '../../shared_components/CustomerBenefit/CustomerBenefit';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import ShoppingBasketGovernmentFee from '../ShoppingBasketPage/components/ShoppingBasketGovernmentFee';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';
import { isAnyEdiCancellationCasePending } from '../../utils/externalCasesUtil';
import ReceiptHeader from './ReceiptHeader';
import ReceiptPendingHeader from './ReceiptPendingHeader';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import { BOUND } from '../../recoil/types/types';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import CoverableInfoList from './CoverableInfoList';
import ReceiptHeaderCVR from './ReceiptHeaderCVR';
import type { ContainsSpinOffProducts } from '../../recoil/types/daTypes';
import { useHippoText } from '../../hooks/useHippoText';
import { filterGovernmentFees } from '../ShoppingBasketPage/utils/ShoppingBasketUtil';
import {
    doesCoverablesContainChangedStatus,
    isAccCoverableForOtherPerson
} from '../../utils/coverablesUtil';
import type { PrhCommonDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import { hideInfoInMpsSelector } from '../../recoil/selectors/hideInfoInMpsSelector';
import { getOPCReceiptPageChangeTypeAtom } from '../../recoil/atoms/getOPCReceiptPageChangeTypeAtom';

const ReceiptPage: WizardPageComponent = () => {
    const getHippoText = useHippoText();
    const jobData = useRecoilValue(jobAtom);
    const priceTaxData = useRecoilValue(priceTaxAtom);
    const externalCases = useRecoilValue(externalCasesAtom);
    const { email, isCVR } = useRecoilValue(accountHolderAtom);
    const { sortedCoverables, sortedRemovedCoverables } = useRecoilValue(
        sortedCoverablesListSelector
    );
    const shouldHideInfoInMps = useRecoilValue(hideInfoInMpsSelector);
    const getOPCReceiptPageChangeType = useRecoilValue(getOPCReceiptPageChangeTypeAtom);
    const isQuoteAlreadyIssued = jobData.jobStatus === BOUND;
    const hasCoverableWithChangedStatus = doesCoverablesContainChangedStatus(sortedCoverables);
    const isOPCChange =
        getOPCReceiptPageChangeType === 'adjustCoverage' ||
        getOPCReceiptPageChangeType === 'addCoverage' ||
        getOPCReceiptPageChangeType === 'carOdometerReading';

    if (!sortedCoverables) {
        return <LoadingDots />;
    }

    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const isPaymentYearly = selectedPaymentFrequency === paymentFrequencyMap.annual;
    const isPaymentOnetime = selectedPaymentFrequency === paymentFrequencyMap.onetime;
    const paymentFrequencyPostfix = periodicityUnit[selectedPaymentFrequency];
    const filteredGovernmentFees = filterGovernmentFees(priceTaxData.annualTaxes ?? []);
    const allCoverables = sortedCoverables.concat(sortedRemovedCoverables);
    const areAllCoverablesSpunOff =
        allCoverables?.length > 0 && allCoverables.every((cov) => cov.willSpinOff ?? false);
    const doesContainAccForOtherPerson = allCoverables.some((cov) =>
        isAccCoverableForOtherPerson(cov)
    );
    const containsOnlyAccForOtherPerson = allCoverables.every((cov) =>
        isAccCoverableForOtherPerson(cov)
    );

    const hasPendingReportOrHouseUWI = allCoverables.find(
        (cov) => (cov as PrhCommonDTO)?.outputBuildingReportPending || (cov as PrhCommonDTO)?.hasUWI
    );

    let newInsurances = 0;
    let changedInsurances = 0;

    const getGroupedCoverables = (): {
        spinOffAccidentCoverables: CoverableDTO[];
        restOfCoverables: CoverableDTO[];
    } => {
        const spinOffAccidentCoverables: CoverableDTO[] = [];
        const restOfCoverables: CoverableDTO[] = [];

        sortedCoverables.forEach((coverable) => {
            if (isAccCoverableForOtherPerson(coverable)) {
                spinOffAccidentCoverables.push(coverable);
            } else {
                restOfCoverables.push(coverable);
            }

            if (coverable.status === 'Added') {
                newInsurances++;
            } else {
                changedInsurances++;
            }
        });
        return { spinOffAccidentCoverables, restOfCoverables };
    };

    const { spinOffAccidentCoverables, restOfCoverables } = getGroupedCoverables();

    const containsSpinOffProducts = (): ContainsSpinOffProducts => {
        if (spinOffAccidentCoverables.length > 0) {
            return restOfCoverables.length > 0 ? 'MixSpinOff' : 'OnlySpinOff';
        }
        return 'NoSpinOff';
    };

    return (
        <>
            {isCVR ? (
                <ReceiptHeaderCVR coverableAmount={sortedCoverables.length} email={email ?? ''} />
            ) : isAnyEdiCancellationCasePending(externalCases) ||
              (areAllCoverablesSpunOff &&
                  !hasPendingReportOrHouseUWI &&
                  !doesContainAccForOtherPerson) ? (
                <ReceiptPendingHeader
                    isQuoteAlreadyIssued={isQuoteAlreadyIssued}
                    policyHolderEmail={email ?? ''}
                    coverableAmount={sortedCoverables.length}
                />
            ) : (
                <ReceiptHeader
                    newInsurances={newInsurances}
                    changedInsurances={changedInsurances}
                    containsSpinOffProducts={containsSpinOffProducts()}
                />
            )}
            {!shouldHideInfoInMps && getOPCReceiptPageChangeType !== 'updateMissingAttribute' && (
                <div className="form-group">
                    {!containsOnlyAccForOtherPerson && (
                        <section className="Section Section--compact">
                            <Box>
                                <div className="u-flex-box u-flex-box--space-between u-flex-box--nowrap">
                                    <p className="u-spacing-stack-s paragraph--small">Kvittering</p>
                                    {isOPCChange && (
                                        <p className="u-spacing-stack-s paragraph--small u-text-right">
                                            {getHippoText(
                                                'DSS-RECEIPT-PAGE-EXISTING-CUSTOMER-NEW-PRICE'
                                            )}
                                        </p>
                                    )}
                                </div>
                                <CoverableInfoList coverables={restOfCoverables} />
                                <CustomerBenefit interactive={false} />
                                {filteredGovernmentFees?.length > 0 &&
                                    (isPaymentYearly || isPaymentOnetime) && (
                                        <div className="u-spacing-inset-m--stretch-max">
                                            <ShoppingBasketGovernmentFee
                                                governmentFees={filteredGovernmentFees}
                                            />
                                        </div>
                                    )}
                                {!hasCoverableWithChangedStatus && (
                                    <TotalPrice
                                        totalPrice={
                                            selectedPaymentFrequency === paymentFrequencyMap.monthly
                                                ? priceTaxData.monthlyPrice?.amount ?? 0
                                                : priceTaxData.yearlyPriceWithFees?.amount ?? 0
                                        }
                                        showYearlyPriceField={
                                            selectedPaymentFrequency === paymentFrequencyMap.monthly
                                        }
                                        paymentFrequencyPostfix={paymentFrequencyPostfix}
                                        displayTaxesSubtitle
                                        shouldShowPriceIncludingTaxes={
                                            selectedPaymentFrequency !== paymentFrequencyMap.monthly
                                        }
                                        isCVR={isCVR}
                                    />
                                )}
                            </Box>
                        </section>
                    )}
                    {spinOffAccidentCoverables.length > 0 && (
                        <section className="Section Section--no-space-above">
                            <Box>
                                <p className="u-color-supplement-error-base paragraph--small">
                                    Forsikring, du har bestilt til en anden person
                                </p>
                                <CoverableInfoList coverables={spinOffAccidentCoverables} />
                            </Box>
                        </section>
                    )}
                    {sortedRemovedCoverables.length > 0 && (
                        <section className="Section Section--no-space-above">
                            <Box>
                                <p className="u-color-supplement-error-base paragraph--small">
                                    {getHippoText(
                                        'DSS-RECEIPT-PAGE-TERMINATED-INSURANCES-SUBHEADER'
                                    )}
                                </p>
                                <CoverableInfoList coverables={sortedRemovedCoverables} />
                            </Box>
                        </section>
                    )}
                    {filteredGovernmentFees?.length > 0 &&
                        !isPaymentYearly &&
                        !isPaymentOnetime && (
                            <section className="Section Section--no-space-above">
                                <Box>
                                    <div className="u-spacing-inset-m--stretch-max">
                                        <ShoppingBasketGovernmentFee
                                            governmentFees={filteredGovernmentFees}
                                        />
                                    </div>
                                </Box>
                            </section>
                        )}
                </div>
            )}
        </>
    );
};

export default ReceiptPage;
